import {IProcessFlowMappedVariable, IProcessFlowVariableMap} from '~/interfaces/processFlowInterfaces'
import {ProcessFlowMappedVariableType, ProcessFlowMappedVariableTypes, ProcessFlowMappedVariableTypesAttachments, ProcessFlowMappedVariableTypesBoolean, ProcessFlowMappedVariableTypesBooleanCollection, ProcessFlowMappedVariableTypesObjects, ProcessFlowMappedVariableTypesPickList, ProcessFlowMappedVariableTypesSignature, ProcessFlowMappedVariableTypesTextCollection} from '../../../formability/processFlowBuilder/enums/processFlowMappedVariableType'
import Formula from '~/models/formula'
import {Constructor} from '../../../global/constructor'
import {RelationType} from '~/models/relationType'
import {Hash} from '~/interfaces/hashInterfaces'
import {ReferencePath} from '~/models/referencePaths/referencePath'
import {IReferencePath, IReferencePathNodeSpecific, IReferencePathReferenceDetails, ReferencePathReferenceDetailsType} from '~/interfaces/referencePathInterfaces'
import {ModelType} from '~/enums/modelType'
import assertNever from '../../../global/assetNever'

export type ProcessFlowMappedVariableClasses =
  'ProcessFlowMappedVariable'
  | 'CertificateInputMap'
  | 'IterationStepMappedVariable'

type TCreateEmbeddedDefinition = {
  source_variable_path: (parent, json: Hash) => IReferencePath
  formula_reference_paths: (parent, json: Hash) => IReferencePath
  source_constant_path: (parent, json: Hash) => IReferencePath
} & Hash<(parent, json) => any>

export default class ProcessFlowMappedVariableDefinition {
  /**
   * Means something has been selected
   */
  static pathExists(mappedVariable: IProcessFlowMappedVariable): boolean {
    switch (mappedVariable.source_type) {
      case ProcessFlowMappedVariableType.Variable:
        return Boolean(mappedVariable.source_variable_path?.nodesHandler.isPresent)
      case ProcessFlowMappedVariableType.Formula:
        // TODO: Could check that all variables in pills exist
        return true
      case ProcessFlowMappedVariableType.Constant:
    }
  }

  /**
   * Means there is actually a reference of some kind for every node
   */
  static variableExists(mappedVariable: IProcessFlowMappedVariable): boolean {
    switch (mappedVariable.source_type) {
      case ProcessFlowMappedVariableType.Variable:
        return Boolean(mappedVariable.source_variable_path?.allReferencesExist)
      case ProcessFlowMappedVariableType.Formula:
        // TODO: Could check that all variables in pills exist
        return true
      case ProcessFlowMappedVariableType.Constant:
        return true
    }
  }

  /**
   * Means there is actually a reference of some kind for every node and the nodes are linked
   * e.g. model relation to model checks that the relation actually has that model in the relation object models collection
   */
  static allReferencesLinked(mappedVariable: IProcessFlowMappedVariable): boolean {
    switch (mappedVariable.source_type) {
      case ProcessFlowMappedVariableType.Variable:
        return Boolean(mappedVariable.source_variable_path?.allReferencesLinked)
      case ProcessFlowMappedVariableType.Formula:
        // TODO: Could check that all variables in pills are completely linked
        return true
      case ProcessFlowMappedVariableType.Constant:
        return true
    }
  }

  // Sheamus: what does this do? -- pulled from another location (generate certificate step)
  // Alex: This removes paths/nodes that are no longer needed in a formula.
  //  If user removes a variable in a formula, this will clean up the path/nodes saved for it.
  static cleanUpPaths(mappedVariable: IProcessFlowMappedVariable): void {
    if (mappedVariable.source_formula) {
      const pathClientIds = Formula.parseClientIds(mappedVariable.source_formula)
      for (const path of mappedVariable.formulaReferencePathsHandler.all) {
        if (!pathClientIds.includes(path.client_id)) {
          path.destroy()
        }
      }
    }
  }

  /**
   * For establishing embedded models configurations on ProcessFlowMappedVariable and inherited classes
   */
  static embeddedModelDefinitionFactory(className: ProcessFlowMappedVariableClasses): Hash<{ type: Constructor, relationType: RelationType }> {

    const definitions: Hash<{ type: Constructor, relationType: RelationType }> = {
      source_variable_path: { type: ReferencePath, relationType: RelationType.singleObject },
      source_constant_path: { type: ReferencePath, relationType: RelationType.singleObject },
      formula_reference_paths: { type: ReferencePath, relationType: RelationType.dictionary },
    }

    // for custom definitions per class (similar to ProcessFlowStep version of this)
    switch (className) {
      case 'ProcessFlowMappedVariable':
        break
      case 'CertificateInputMap':
        break
      default:
        break
    }

    return definitions
  }

  static createEmbeddedDefinitionFactory(mappedVariable: IProcessFlowMappedVariable): TCreateEmbeddedDefinition {
    const createEmbedded: TCreateEmbeddedDefinition = {
      source_variable_path: (parent = this, json: Hash = {}): ReferencePath => {
        return ReferencePath.repo.findOrCreateWith(json.client_id, parent, json, { nameInParent: 'source_variable_path', isCollectionRoot: false}).loadFromJson(json);
      },
      formula_reference_paths: (parent = this, json: Hash = {}): ReferencePath => {
        return ReferencePath.repo.findOrCreateWith(json.client_id, parent, json, { nameInParent: 'formula_reference_paths', isCollectionRoot: true}).loadFromJson(json);
      },
      source_constant_path: (parent = this, json: Hash = {}): ReferencePath => {
        return ReferencePath.repo.findOrCreateWith(json.client_id, parent, json, { nameInParent: 'source_constant_path', isCollectionRoot: false}).loadFromJson(json);
      },
    }

    // for custom definitions per class (similar to ProcessFlowStep version of this)
    switch (mappedVariable.className) {
      case 'ProcessFlowMappedVariable':
        break
      case 'CertificateInputMap':
        break
      default:
        break
    }

    return createEmbedded
  }

  static valueReferenceVariableTypesFor(node: IReferencePathNodeSpecific): readonly ProcessFlowMappedVariableType[] {
    if (node === undefined) return [...ProcessFlowMappedVariableTypes]
    if (!node.service.reference()) return []
    switch (node.service.referenceGenericModelType) {
      case ModelType.File:
        return ProcessFlowMappedVariableTypesAttachments
      case ModelType.Text: // intentional fallthrough
      case ModelType.Number: // intentional fallthrough
      case ModelType.DateTime:
        if (node.service.referenceIsPickList()) { // single or multi pick list
          return ProcessFlowMappedVariableTypesPickList
        } else if (node.service.referenceIsCollection) {
          return ProcessFlowMappedVariableTypesTextCollection
        } else {
          return ProcessFlowMappedVariableTypes
        }
      case ModelType.Object:
        if (node.service.referenceModelType === ModelType.User) {
          return ProcessFlowMappedVariableTypes
        }
        return ProcessFlowMappedVariableTypesObjects
      case ModelType.Array:
        throw new Error('ModelType.Array not implemented')
      case ModelType.Boolean:
        if (node.service.referenceIsCollection) {
          return ProcessFlowMappedVariableTypesBooleanCollection
        } else {
          return ProcessFlowMappedVariableTypesBoolean
        }
      case ModelType.Signature:
        return ProcessFlowMappedVariableTypesSignature
      default:
        assertNever(node.service.referenceGenericModelType)
    }
    return []
  }

  static valueConstantDetails(mappedVariable: IProcessFlowMappedVariable, destinationValue: IProcessFlowMappedVariable | IProcessFlowVariableMap): IReferencePathReferenceDetails {
    let details: IReferencePathReferenceDetails
    let destinationMappedVariable: IProcessFlowMappedVariable = this.getMappedVariable(destinationValue)
    switch (destinationMappedVariable?.source_type) {
      case ProcessFlowMappedVariableType.Variable:
        if (destinationMappedVariable.source_variable_path?.lastNodeService) {
          details = destinationMappedVariable.source_variable_path.lastNodeService.referenceDetails
        }
        if (details?.type === ReferencePathReferenceDetailsType.Boolean) {
          details.options.selectedOption = details.options.options.find(o => o.value === mappedVariable.source_constant)
        }
        break
      default:
        details = { type: ReferencePathReferenceDetailsType.Text }
        break
    }

    if (details.type === ReferencePathReferenceDetailsType.DateTimePicker) {
      details.timezone = mappedVariable.organization.time_zone
    }
    return details
  }

  private static getMappedVariable(value: IProcessFlowMappedVariable | IProcessFlowVariableMap): IProcessFlowMappedVariable {
    if ('variable_reference' in value) {
      return value.variable_reference
    } else {
      return value
    }
  }
}